import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase/firebaseConfig";
import {
  doc,
  getDoc,
  collection,
  addDoc,
  getDocs,
  updateDoc,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  Edit,
  Upload,
  PlayCircle,
  Save,
  Loader2,
  ArrowLeft,
  Video,
} from "lucide-react";

const AdminCourseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({
    title: "",
    description: "",
    duration: "",
    video: null,
  });

  // Fetch course detail
  useEffect(() => {
    const fetchCourse = async () => {
      const docRef = doc(db, "courses", id);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) setCourse(docSnap.data());
    };
    fetchCourse();
  }, [id]);

  // Fetch episodes
  useEffect(() => {
    const fetchEpisodes = async () => {
      const querySnap = await getDocs(
        collection(db, "courses", id, "episodes")
      );
      setEpisodes(querySnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    };
    fetchEpisodes();
  }, [id]);

  // Upload episode
  const handleUploadEpisode = async () => {
    if (!uploadData.video) return alert("Please select a video!");
    setUploading(true);
    try {
      const videoRef = ref(
        storage,
        `courses/${id}/episodes/${Date.now()}-${uploadData.video.name}`
      );
      const uploadTask = uploadBytesResumable(videoRef, uploadData.video);
      uploadTask.on(
        "state_changed",
        null,
        (error) => console.error(error),
        async () => {
          const videoUrl = await getDownloadURL(uploadTask.snapshot.ref);
          await addDoc(collection(db, "courses", id, "episodes"), {
            ...uploadData,
            videoUrl,
            createdAt: new Date(),
          });
          setUploadData({
            title: "",
            description: "",
            duration: "",
            video: null,
          });
          alert("Episode uploaded successfully!");
        }
      );
    } catch (err) {
      console.error(err);
      alert("Error uploading episode");
    } finally {
      setUploading(false);
    }
  };

  // Update status
  const handleStatusUpdate = async () => {
    const newStatus = course.status === "comingsoon" ? "live" : "comingsoon";
    await updateDoc(doc(db, "courses", id), { status: newStatus });
    setCourse((prev) => ({ ...prev, status: newStatus }));
  };

  if (!course) return <Loading>Loading course...</Loading>;

  return (
    <Wrapper>
      <Header>
        <button className="back" onClick={() => navigate("/dashboard")}>
          <ArrowLeft /> Back
        </button>
        <div className="title-block">
          <h1>{course.title}</h1>
          <StatusTag status={course.status}>
            {course.status === "live" ? "LIVE" : "COMING SOON"}
          </StatusTag>
        </div>
        <div className="actions">
          <button
            className="edit"
            onClick={() => navigate(`/edit-course/${id}`)}
          >
            <Edit size={18} /> Edit Course
          </button>
          <button className="toggle" onClick={handleStatusUpdate}>
            <Save size={18} /> Set{" "}
            {course.status === "comingsoon" ? "Live" : "Coming Soon"}
          </button>
        </div>
      </Header>

      <MainGrid>
        {/* LEFT: Course Info */}
        <CourseCard>
          <img src={course.imageUrl} alt={course.title} />
          <div className="details">
            <h2>{course.title}</h2>
            <p>{course.description}</p>
            <InfoRow>
              <span>💰 Price:</span> ₹{course.price}
            </InfoRow>
            <InfoRow>
              <span>📚 Category:</span> {course.category}
            </InfoRow>
            <InfoRow>
              <span>🎥 Total Episodes:</span> {episodes.length}
            </InfoRow>
          </div>
        </CourseCard>

        {/* RIGHT: Upload Episode */}
        <UploadCard>
          <h3>🎬 Upload New Episode</h3>
          <input
            type="text"
            placeholder="Episode Title"
            value={uploadData.title}
            onChange={(e) =>
              setUploadData({ ...uploadData, title: e.target.value })
            }
          />
          <textarea
            placeholder="Episode Description"
            value={uploadData.description}
            onChange={(e) =>
              setUploadData({ ...uploadData, description: e.target.value })
            }
          />
          <input
            type="text"
            placeholder="Duration (e.g. 10:25)"
            value={uploadData.duration}
            onChange={(e) =>
              setUploadData({ ...uploadData, duration: e.target.value })
            }
          />
          <label className="file-upload">
            <input
              type="file"
              accept="video/*"
              onChange={(e) =>
                setUploadData({ ...uploadData, video: e.target.files[0] })
              }
            />
            <Video size={18} /> Select Video File
          </label>
          <button onClick={handleUploadEpisode} disabled={uploading}>
            {uploading ? <Loader2 className="spin" /> : <Upload />} Upload
            Episode
          </button>
        </UploadCard>
      </MainGrid>

      {/* EPISODES SECTION */}
      <SectionTitle>📺 Course Episodes</SectionTitle>
      <EpisodesGrid>
        {episodes.length === 0 ? (
          <EmptyBox>No episodes uploaded yet.</EmptyBox>
        ) : (
          episodes.map((ep) => (
            <EpisodeCard key={ep.id}>
              <div className="icon">
                <PlayCircle size={30} />
              </div>
              <div className="info">
                <h4>{ep.title}</h4>
                <p>{ep.description}</p>
                <span>{ep.duration}</span>
              </div>
            </EpisodeCard>
          ))
        )}
      </EpisodesGrid>
    </Wrapper>
  );
};

export default AdminCourseDetail;

/* ---------------------- STYLES ---------------------- */

const Wrapper = styled.div`
  background: #0b0b0f;
  color: #fff;
  min-height: 100vh;
  padding: 2rem 3rem;
  font-family: "Inter", sans-serif;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  .back {
    background: transparent;
    border: none;
    color: #aaa;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  .title-block h1 {
    font-size: 1.8rem;
    color: #fff;
  }

  .actions {
    display: flex;
    gap: 1rem;
  }

  .actions button {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.6rem 1rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: 0.2s;
  }

  .edit {
    background: rgba(0, 240, 255, 0.15);
    color: #00f0ff;
  }

  .edit:hover {
    background: rgba(0, 240, 255, 0.3);
  }

  .toggle {
    background: rgba(255, 180, 0, 0.2);
    color: #ffb400;
  }

  .toggle:hover {
    background: rgba(255, 180, 0, 0.4);
  }
`;

const StatusTag = styled.span`
  background: ${(p) =>
    p.status === "live" ? "rgba(0,255,150,0.2)" : "rgba(255,0,0,0.2)"};
  color: ${(p) => (p.status === "live" ? "#00ff99" : "#ff4d4d")};
  padding: 0.3rem 0.8rem;
  border-radius: 6px;
  font-size: 0.8rem;
  margin-left: 0.6rem;
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const CourseCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);

  img {
    width: 200px;
    height: 130px;
    border-radius: 12px;
    object-fit: cover;
  }

  .details h2 {
    font-size: 1.3rem;
    color: #00f0ff;
  }

  p {
    color: #bbb;
    margin: 0.4rem 0;
  }
`;

const InfoRow = styled.div`
  margin-top: 0.3rem;
  color: #ddd;
  span {
    color: #00f0ff;
    font-weight: 500;
  }
`;

const UploadCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;

  h3 {
    color: #00f0ff;
    margin-bottom: 0.5rem;
  }

  input,
  textarea {
    background: rgba(255, 255, 255, 0.08);
    border: none;
    padding: 0.6rem 1rem;
    border-radius: 6px;
    color: #fff;
  }

  textarea {
    height: 70px;
  }

  .file-upload {
    background: rgba(255, 255, 255, 0.08);
    border-radius: 6px;
    padding: 0.6rem 1rem;
    display: flex;
    align-items: center;
    gap: 0.6rem;
    cursor: pointer;
    color: #ccc;
  }

  .file-upload:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  .file-upload input {
    display: none;
  }

  button {
    background: linear-gradient(135deg, #00f0ff, #00bfff);
    border: none;
    color: #000;
    padding: 0.6rem 1rem;
    border-radius: 6px;
    cursor: pointer;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const SectionTitle = styled.h2`
  margin-top: 3rem;
  margin-bottom: 1rem;
  color: #00f0ff;
  font-size: 1.4rem;
`;

const EpisodesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const EpisodeCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  transition: 0.3s;
  cursor: pointer;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    transform: translateY(-3px);
  }

  .icon {
    color: #00f0ff;
  }

  .info h4 {
    font-size: 1rem;
    color: #fff;
  }

  .info p {
    color: #aaa;
    font-size: 0.85rem;
    margin: 0.3rem 0;
  }

  span {
    color: #00f0ff;
    font-size: 0.8rem;
  }
`;

const EmptyBox = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 2rem;
  border-radius: 12px;
  text-align: center;
  color: #aaa;
`;

const Loading = styled.div`
  text-align: center;
  padding: 4rem;
  color: #ccc;
`;
